import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
    Clock, MapPin, ChevronLeft, ChevronRight, Cloud, Trophy, 
    AlertTriangle, Calendar, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format going condition
const formatGoing = (going) => {
    if (!going || going === 'Unknown') return 'Unknown';
    return going
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .replace('To ', 'to ');
};

// Race Time Tab Component
const RaceTimeTab = ({ event, isSelected, onClick, isFinished, isAbandoned }) => {
    const time = format(parseISO(event.start), 'HH:mm');
    
    return (
        <button
            onClick={onClick}
            disabled={isAbandoned}
            className={cn(
                "flex flex-col items-center px-4 py-2 rounded-lg transition-all shrink-0 min-w-[70px]",
                isSelected 
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                    : "bg-muted/50 hover:bg-muted",
                isFinished && !isSelected && "border-l-2 border-amber-500",
                isAbandoned && "opacity-40 cursor-not-allowed"
            )}
            data-testid={`race-tab-${event.id}`}
        >
            <span className="font-data text-lg font-bold">{time}</span>
            {isFinished && (
                <span className="text-[10px] uppercase tracking-wide opacity-80">Result</span>
            )}
        </button>
    );
};

// Navigation Arrows for Race Times
const RaceNavigation = ({ events, selectedIndex, onSelect, isAbandoned }) => {
    const scrollRef = useRef(null);
    
    const scrollToSelected = () => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const selectedTab = container.children[selectedIndex];
            if (selectedTab) {
                const containerWidth = container.offsetWidth;
                const tabLeft = selectedTab.offsetLeft;
                const tabWidth = selectedTab.offsetWidth;
                const scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);
                container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            }
        }
    };
    
    useEffect(() => {
        scrollToSelected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);
    
    const canGoPrev = selectedIndex > 0;
    const canGoNext = selectedIndex < events.length - 1;
    
    const handlePrev = () => {
        if (canGoPrev) onSelect(selectedIndex - 1);
    };
    
    const handleNext = () => {
        if (canGoNext) onSelect(selectedIndex + 1);
    };

    return (
        <div className="flex items-center gap-2 w-full">
            {/* Previous Button */}
            <Button
                variant="outline"
                size="icon"
                onClick={handlePrev}
                disabled={!canGoPrev || isAbandoned}
                className="shrink-0 rounded-full"
                data-testid="prev-race-btn"
            >
                <ChevronLeft className="h-5 w-5" />
            </Button>
            
            {/* Race Time Tabs */}
            <div 
                ref={scrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 py-1"
            >
                {events.map((event, idx) => (
                    <RaceTimeTab
                        key={event.id}
                        event={event}
                        isSelected={idx === selectedIndex}
                        onClick={() => onSelect(idx)}
                        isFinished={event.status?.includes('WEIGHED_IN') || event.status?.includes('RESULT')}
                        isAbandoned={isAbandoned}
                    />
                ))}
            </div>
            
            {/* Next Button */}
            <Button
                variant="outline"
                size="icon"
                onClick={handleNext}
                disabled={!canGoNext || isAbandoned}
                className="shrink-0 rounded-full"
                data-testid="next-race-btn"
            >
                <ChevronRight className="h-5 w-5" />
            </Button>
        </div>
    );
};

// Race Summary Card (for non-selected races)
const RaceSummaryCard = ({ event, onClick, isAbandoned }) => {
    const time = format(parseISO(event.start), 'HH:mm');
    const raceClass = event.meta?.['event:class']?.value;
    const isFinished = event.status?.includes('WEIGHED_IN') || event.status?.includes('RESULT');
    
    return (
        <Card 
            className={cn(
                "card-hover cursor-pointer",
                isAbandoned && "opacity-50"
            )}
            onClick={() => !isAbandoned && onClick()}
            data-testid={`race-summary-${event.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-center gap-4">
                    <div className={cn(
                        "font-data text-xl font-bold px-3 py-1 rounded-lg",
                        isFinished ? "bg-amber-500/20 text-amber-600" : "bg-primary/10 text-primary"
                    )}>
                        {time}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{event.title}</p>
                        <p className="text-sm text-muted-foreground">
                            {raceClass && `Class ${raceClass}`}
                            {isFinished && <Badge variant="outline" className="ml-2 text-xs">Result</Badge>}
                        </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
            </CardContent>
        </Card>
    );
};

export default function MeetingPage() {
    const { stageId } = useParams();
    const navigate = useNavigate();
    const [meeting, setMeeting] = useState(null);
    const [selectedRaceIndex, setSelectedRaceIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMeeting = async () => {
            setLoading(true);
            try {
                // Get user's saved regions or default to common ones
                let regions = ['UK', 'IRE', 'FR', 'USA', 'ZA', 'AUS'];
                const storedRegions = localStorage.getItem('racewise_regions');
                if (storedRegions) {
                    try {
                        regions = JSON.parse(storedRegions);
                    } catch (e) {
                        // Use defaults
                    }
                }
                
                // Search across all regions to find the meeting
                let foundMeeting = null;
                
                for (const region of regions) {
                    if (foundMeeting) break;
                    
                    const response = await axios.get(`${API}/racing/stages`, {
                        params: { country: region }
                    });
                    const meetings = response.data.items || [];
                    
                    // Find the meeting by ID
                    foundMeeting = meetings.find(m => m.id === stageId);
                }
                
                // If still not found, try all supported regions as fallback
                if (!foundMeeting) {
                    const allRegions = ['UK', 'IRE', 'FR', 'USA', 'ZA', 'AUS'];
                    for (const region of allRegions) {
                        if (foundMeeting) break;
                        if (regions.includes(region)) continue; // Already searched
                        
                        const response = await axios.get(`${API}/racing/stages`, {
                            params: { country: region }
                        });
                        const meetings = response.data.items || [];
                        foundMeeting = meetings.find(m => m.id === stageId);
                    }
                }
                
                if (foundMeeting) {
                    setMeeting(foundMeeting);
                    
                    // Find the first upcoming race or default to first race
                    const now = new Date();
                    const upcomingIndex = foundMeeting.events?.findIndex(e => 
                        parseISO(e.start) > now
                    );
                    setSelectedRaceIndex(upcomingIndex >= 0 ? upcomingIndex : 0);
                }
            } catch (error) {
                console.error('Failed to fetch meeting:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMeeting();
    }, [stageId]);

    const handleRaceSelect = (index) => {
        setSelectedRaceIndex(index);
    };

    const handleViewFullRacecard = (eventId) => {
        navigate(`/race/${eventId}`, { state: { courseName: meeting?.name } });
    };

    // Check if meeting is abandoned - look for ABANDONED status in events
    const checkAbandoned = () => {
        if (!meeting) return false;
        const events = meeting.events || [];
        if (events.length === 0) return false;
        const abandonedStatuses = ['ABANDONED', 'EVENT_ABANDONED', 'MEETING_ABANDONED'];
        return events.every(e => 
            abandonedStatuses.some(s => (e.status || '').toUpperCase().includes(s))
        );
    };
    const isAbandoned = checkAbandoned();
    const going = formatGoing(meeting?.meta?.['stage:going']?.value);
    const weather = meeting?.meta?.['stage:weather']?.value;
    const events = meeting?.events || [];
    const selectedEvent = events[selectedRaceIndex];

    if (loading) {
        return (
            <div className="min-h-screen pb-20 md:pb-8">
                <div className="container-app py-8">
                    <Skeleton className="h-8 w-48 mb-4" />
                    <Skeleton className="h-12 w-full mb-6" />
                    <Skeleton className="h-64 w-full mb-4" />
                    <div className="space-y-3">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-20 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (!meeting) {
        return (
            <div className="min-h-screen pb-20 md:pb-8">
                <div className="container-app py-8 text-center">
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h2 className="font-heading text-xl font-semibold mb-2">Meeting not found</h2>
                    <p className="text-muted-foreground mb-4">
                        This meeting may have been cancelled or hasn't started yet.
                    </p>
                    <Link to="/">
                        <Button className="btn-pill gap-2">
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-6">
                {/* Back Button */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4"
                    data-testid="back-home-btn"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Home
                </Link>

                {/* Meeting Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h1 className="font-heading text-2xl md:text-3xl font-bold page-title">{meeting.name}</h1>
                        {isAbandoned && (
                            <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Abandoned
                            </Badge>
                        )}
                    </div>
                    
                    {/* Meeting Info */}
                    <div className="flex flex-wrap gap-3 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {format(parseISO(meeting.start), 'EEEE, d MMMM yyyy')}
                        </div>
                        {!isAbandoned && (
                            <>
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <MapPin className="h-4 w-4" />
                                    {going}
                                </div>
                                {weather && (
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <Cloud className="h-4 w-4" />
                                        {weather}
                                    </div>
                                )}
                            </>
                        )}
                        <Badge variant="secondary" className="font-data">
                            {events.length} races
                        </Badge>
                    </div>
                </div>

                {/* Abandoned Meeting Notice */}
                {isAbandoned && (
                    <Card className="mb-6 border-destructive/30 bg-destructive/5">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                                <div>
                                    <p className="font-semibold text-destructive">Meeting Abandoned</p>
                                    <p className="text-sm text-muted-foreground">
                                        This meeting has been abandoned due to adverse weather conditions. 
                                        No races will take place at this venue today.
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Race Time Navigation */}
                {!isAbandoned && events.length > 0 && (
                    <Card className="mb-6 sticky top-16 z-40 bg-background/95 backdrop-blur-md">
                        <CardContent className="p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground font-medium">Race Times</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                    Race {selectedRaceIndex + 1} of {events.length}
                                </span>
                            </div>
                            <RaceNavigation
                                events={events}
                                selectedIndex={selectedRaceIndex}
                                onSelect={handleRaceSelect}
                                isAbandoned={isAbandoned}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Selected Race Preview */}
                {!isAbandoned && selectedEvent && (
                    <Card className="mb-6 border-primary/30 bg-primary/5" data-testid="selected-race-card">
                        <CardContent className="p-5">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge variant="outline" className="font-data text-lg px-3 py-1">
                                            {format(parseISO(selectedEvent.start), 'HH:mm')}
                                        </Badge>
                                        {selectedEvent.status?.includes('WEIGHED_IN') && (
                                            <Badge variant="secondary" className="gap-1">
                                                <Trophy className="h-3 w-3" />
                                                Result
                                            </Badge>
                                        )}
                                    </div>
                                    <h2 className="font-heading text-xl font-semibold mb-1">
                                        {selectedEvent.title}
                                    </h2>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedEvent.meta?.['event:class']?.value && 
                                            `Class ${selectedEvent.meta['event:class'].value}`}
                                        {selectedEvent.meta?.['event:distance:string']?.value && 
                                            ` • ${selectedEvent.meta['event:distance:string'].value}`}
                                    </p>
                                </div>
                                <Button 
                                    className="btn-pill gap-2"
                                    onClick={() => handleViewFullRacecard(selectedEvent.id)}
                                    data-testid="view-racecard-btn"
                                >
                                    View Full Racecard
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Other Races List */}
                {!isAbandoned && events.length > 0 && (
                    <div>
                        <h3 className="font-heading text-lg font-semibold mb-4">
                            {events.length > 1 ? 'All Races Today' : 'Today\'s Race'}
                        </h3>
                        <div className="space-y-3">
                            {events.map((event, idx) => (
                                <div
                                    key={event.id}
                                    className={cn(
                                        "transition-all",
                                        idx === selectedRaceIndex && "ring-2 ring-primary ring-offset-2 ring-offset-background rounded-lg"
                                    )}
                                >
                                    <RaceSummaryCard
                                        event={event}
                                        onClick={() => handleViewFullRacecard(event.id)}
                                        isAbandoned={isAbandoned}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
